import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { importanceMeta, STATUS_LABEL, fmtDateTime } from "@/lib/taskUi";
import { Star, CheckCircle2, Circle, AlertTriangle, User, Eye, Lock } from "lucide-react";
import { DEFAULT_ASSIGNMENT_LABELS } from "@/lib/defaultAssignment";
import { toast } from "sonner";

export function TaskRow({ task, onChanged, showTop10 = true, showAssignee = false, backTo = null, showVisibility = false, templateMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const origin = backTo || { from: location.pathname + location.search, label: location.pathname.startsWith("/tasks") ? "Tasks" : "Back" };
  const im = importanceMeta(task.importance);
  const done = task.status === "completed" || task.status === "cancelled";

  const toggleComplete = async (e) => {
    e.stopPropagation();
    try { await api.patch(`/tasks/${task.id}`, { status: done ? "in_progress" : "completed" }); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const toggleTop10 = async (e) => {
    e.stopPropagation();
    try { await api.post(`/tasks/${task.id}/top10`, { on: !task.in_top10 }); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid={`task-row-${task.id}`} onClick={() => navigate(`/tasks/detail/${task.id}`, { state: origin })}
      className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
      <button onClick={toggleComplete} data-testid={`task-complete-${task.id}`} className="shrink-0">
        {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300 hover:text-brand" />}
      </button>
      <span title={`${im.label} importance`} className={`shrink-0 inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${im.badge}`}>
        <im.icon className="w-3 h-3 mr-0.5" />{im.label}
      </span>
      <div className="min-w-0 flex-1">
        <p className={`text-sm truncate ${done ? "line-through text-slate-400" : "text-slate-800 font-medium"}`}>{task.title}</p>
        <p className="text-xs text-slate-400 truncate flex items-center gap-2">
          {task.category_label && <span>{task.category_label}</span>}
          {task.person_name && <span>· {task.person_name}</span>}
          {task.project_name && <span>· {task.project_name}</span>}
          {templateMode ? <span className="flex items-center gap-0.5 text-slate-500" data-testid={`task-default-assign-${task.id}`}><User className="w-3 h-3" />{DEFAULT_ASSIGNMENT_LABELS[task.default_assignment] || "Not set"}</span>
            : showAssignee && task.assignee_name && <span className="flex items-center gap-0.5"><User className="w-3 h-3" />{task.assignee_name}</span>}
          {showVisibility && (task.candidate_visible
            ? <span className="inline-flex items-center gap-0.5 rounded px-1.5 text-[10px] font-medium bg-emerald-50 text-emerald-700" data-testid={`task-visibility-${task.id}`}><Eye className="w-3 h-3" />Candidate Visible</span>
            : <span className="inline-flex items-center gap-0.5 rounded px-1.5 text-[10px] bg-slate-100 text-slate-500" data-testid={`task-visibility-${task.id}`}><Lock className="w-3 h-3" />Internal</span>)}
        </p>
      </div>
      {task.is_overdue && (
        <span data-testid={`task-overdue-${task.id}`} className="shrink-0 text-xs font-semibold text-rose-600 flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5" />Overdue {task.days_overdue}d
        </span>
      )}
      <span className="shrink-0 text-xs text-slate-500 w-28 text-right hidden sm:block">{task.due_date ? fmtDateTime(task.due_date) : "No due date"}</span>
      <span className="shrink-0 text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 hidden md:block">{STATUS_LABEL[task.status] || task.status}</span>
      {showTop10 && !done && (
        <button onClick={toggleTop10} data-testid={`task-top10-${task.id}`} className="shrink-0" title="Add to Daily Top 10">
          <Star className={`w-4 h-4 ${task.in_top10 ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-400"}`} />
        </button>
      )}
    </div>
  );
}
